import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, TrendingUp, AlertTriangle, Wrench, Zap, Activity, BarChart3, RefreshCw, Target } from 'lucide-react';
import { AIInsightsPanel } from '@/components/ai/ai-insights-panel';
import { AIChatWidget } from '@/components/ai/ai-chat-widget';
import {
  AIPredictionWidget,
  AIRecommendationWidget,
  AIMetricsWidget,
  AIStatusWidget,
} from '@/components/ai/ai-dashboard-widgets';
import { useGetDevicesQuery } from '@/services';
import { aiService } from '@/services/ai-service';
import { MaintenancePrediction, DeviceHealthScore, EnergyOptimization } from '@/types/ai';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/ai-insights')({
  component: AIInsightsPage,
});

function AIInsightsPage() {
  const [maintenancePredictions, setMaintenancePredictions] = useState<MaintenancePrediction[]>([]);
  const [healthScores, setHealthScores] = useState<DeviceHealthScore[]>([]);
  const [energyOptimizations, setEnergyOptimizations] = useState<EnergyOptimization[]>([]);
  const [loading, setLoading] = useState(true);

  const { data: devicesData } = useGetDevicesQuery({ includeSensors: 'true' });

  useEffect(() => {
    const loadAIData = async () => {
      if (!devicesData?.data) return;

      setLoading(true);
      try {
        const [predictions, health, energy] = await Promise.all([
          aiService.predictMaintenance(devicesData.data),
          aiService.calculateHealthScores(devicesData.data),
          aiService.optimizeEnergy(devicesData.data),
        ]);

        setMaintenancePredictions(predictions);
        setHealthScores(health);
        setEnergyOptimizations(energy);
      } catch (error) {
        console.error('Failed to load AI data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAIData();
  }, [devicesData]);

  const handleRefresh = () => {
    if (devicesData?.data) {
      setLoading(true);
      // Simulate refresh delay
      setTimeout(() => setLoading(false), 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Brain className="h-8 w-8 text-blue-500" />
            AI Insights & Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Intelligent monitoring and optimization for your IoT infrastructure
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Maintenance
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Health
          </TabsTrigger>
          <TabsTrigger value="optimization" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Energy
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* AI Insights Panel */}
            <div className="lg:col-span-2">
              <AIInsightsPanel />
            </div>

            {/* AI Status Widget */}
            <div>
              <AIStatusWidget
                systemHealth={92}
                aiStatus="active"
                lastUpdate={new Date()}
                activeTasks={[
                  'Analyzing sensor patterns',
                  'Training anomaly detection',
                  'Optimizing energy schedules',
                  'Processing maintenance predictions',
                ]}
              />
            </div>
          </div>

          {/* AI Predictions and Recommendations Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            <AIPredictionWidget
              title="System Performance"
              prediction="98.5% uptime expected"
              confidence={94}
              trend="up"
              timeframe="Next 7 days"
            />

            <AIPredictionWidget
              title="Energy Consumption"
              prediction="12% reduction possible"
              confidence={87}
              trend="down"
              timeframe="Next 30 days"
            />

            <AIRecommendationWidget
              title="AI Recommendations"
              recommendations={[
                {
                  action: 'Optimize polling intervals',
                  impact: 'Reduce network load by 25%',
                  effort: 'low',
                  savings: '$150/mo',
                },
                {
                  action: 'Implement predictive scaling',
                  impact: 'Improve response time by 40%',
                  effort: 'medium',
                  savings: '$300/mo',
                },
                {
                  action: 'Upgrade sensor firmware',
                  impact: 'Enhanced security and performance',
                  effort: 'high',
                  savings: '$200/mo',
                },
              ]}
            />
          </div>

          {/* AI Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AIMetricsWidget
              title="AI Performance Metrics"
              metrics={[
                {
                  label: 'Anomaly Detection Accuracy',
                  value: '94.2%',
                  change: 2.1,
                  icon: AlertTriangle,
                  color: 'bg-red-500',
                },
                {
                  label: 'Prediction Confidence',
                  value: '91.8%',
                  change: 1.5,
                  icon: TrendingUp,
                  color: 'bg-blue-500',
                },
                {
                  label: 'Energy Optimization',
                  value: '23%',
                  change: 5.2,
                  icon: Zap,
                  color: 'bg-green-500',
                },
                {
                  label: 'Cost Savings (Monthly)',
                  value: '$2,340',
                  change: 8.7,
                  icon: Activity,
                  color: 'bg-purple-500',
                },
              ]}
            />

            <AIMetricsWidget
              title="System Intelligence"
              metrics={[
                {
                  label: 'Active AI Models',
                  value: '7',
                  icon: Brain,
                  color: 'bg-indigo-500',
                },
                {
                  label: 'Data Points Analyzed',
                  value: '2.4M',
                  change: 15.3,
                  icon: BarChart3,
                  color: 'bg-cyan-500',
                },
                {
                  label: 'Automated Actions',
                  value: '156',
                  change: 12.4,
                  icon: RefreshCw,
                  color: 'bg-orange-500',
                },
                {
                  label: 'Learning Accuracy',
                  value: '96.7%',
                  change: 3.2,
                  icon: Target,
                  color: 'bg-pink-500',
                },
              ]}
            />
          </div>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {maintenancePredictions.map((prediction) => (
              <Card key={prediction.deviceId} className="relative">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Device {prediction.deviceId.substring(0, 8)}...</CardTitle>
                    <Badge
                      className={
                        prediction.priority === 'critical'
                          ? 'bg-red-500'
                          : prediction.priority === 'high'
                            ? 'bg-orange-500'
                            : prediction.priority === 'medium'
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                      }
                    >
                      {prediction.priority}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Component:</span>
                      <span className="font-medium">{prediction.component}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Failure Risk:</span>
                      <span className="font-medium">{Math.round(prediction.failureProbability * 100)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Days Until Maintenance:</span>
                      <span className="font-medium">{prediction.daysUntilMaintenance}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Recommended Actions:</p>
                    <ul className="text-xs space-y-1">
                      {prediction.recommendedActions.map((action, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full bg-current" />
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {healthScores.map((health) => (
              <Card key={health.deviceId}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    Device {health.deviceId.substring(0, 8)}...
                    <Badge
                      className={
                        health.overallHealth >= 90
                          ? 'bg-green-500'
                          : health.overallHealth >= 70
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }
                    >
                      {health.overallHealth}%
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    {Object.entries(health.componentHealth).map(([component, score]) => (
                      <div key={component} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{component}:</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                score >= 90 ? 'bg-green-500' : score >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                          <span className="font-medium w-8 text-right">{score}%</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Trend:</span>
                      <span
                        className={`font-medium ${
                          health.trending === 'improving'
                            ? 'text-green-600'
                            : health.trending === 'declining'
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }`}
                      >
                        {health.trending}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Energy Optimization Tab */}
        <TabsContent value="optimization" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {energyOptimizations.map((optimization) => (
              <Card key={optimization.deviceId}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    Device {optimization.deviceId.substring(0, 8)}...
                    <Badge variant="outline" className="text-blue-600">
                      {Math.round(optimization.savingsPercentage)}% savings
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Current Consumption:</span>
                      <span className="font-medium">{Math.round(optimization.currentConsumption)}W</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Optimized Consumption:</span>
                      <span className="font-medium text-green-600">
                        {Math.round(optimization.optimizedConsumption)}W
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Potential Savings:</span>
                      <span className="font-medium text-blue-600">{Math.round(optimization.potentialSavings)}W</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Optimization Actions:</p>
                    <div className="space-y-1">
                      {optimization.optimizationActions.map((action, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="flex-1">{action.action}</span>
                          <Badge variant="outline" className="text-xs">
                            {action.impact}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
}
